import { CircularBuffer } from './CircularBuffer';
import { getSelector } from '../../attribution/getSelector';

/**
 * Интеракция пользователя с интерфейсом.
 *
 * Интеракция отличается от Event-a тем, что одна интеракция может содержать несколько событий.
 * Например, ввод с клавиатуры это несколько событий, но одна интеракция.
 */
export type Interaction = {
    /** Селектор элемента, с которым произошла интеракция */
    selector: string;
    /** Время, когда произошла интеракция */
    timeStamp: number;
    /** Тип события интеракции */
    eventType: string;
};

/**
 * Собирает пользовательские действия на странице
 */
export class UserInteractionCollector {
    private readonly buffer: CircularBuffer<Interaction>;
    private lastEvent: Event | null = null;
    private started: boolean = false;
    private selectorCache = new WeakMap<EventTarget, string>();

    /**
     * @param events события, за которыми нужно следить
     * @param eventsLimit количество сохраненных событий
     */
    constructor(
        private readonly events: ReadonlyArray<keyof WindowEventMap>,
        eventsLimit: number
    ) {
        this.buffer = new CircularBuffer<Interaction>(eventsLimit);
        this.events.map(eventType => {
            addEventListener(eventType, this.handleEvent, { capture: true });
        });
    }

    private handleEvent = (e: Event) => {
        if (!this.started || !e.target) {
            return;
        }

        // Берем только крайнюю интеракцию с элементом, чтобы не засорять буффер
        const isSameUserInteraction = this.lastEvent?.target === e.target;
        this.lastEvent = e;

        const eventRecord = {
            selector: this.createTargetSelector(e.target),
            timeStamp: e.timeStamp,
            eventType: e.type,
        };

        if (isSameUserInteraction) {
            this.buffer.replaceLast(eventRecord);
        } else {
            this.buffer.push(eventRecord);
        }
    };

    private createTargetSelector(target: EventTarget) {
        if (target instanceof Window) {
            return 'window';
        }

        if (target instanceof Node) {
            const cached = this.selectorCache.get(target);
            if (cached !== undefined) {
                return cached;
            }

            const selector = getSelector(target);
            this.selectorCache.set(target, selector);
            return selector;
        }

        return 'unknown';
    }

    /** Останавливает сбор событий и очищает собранные события */
    stop() {
        this.started = false;
        this.buffer.clear();
    }

    /** Начинает сбор событий */
    start() {
        this.started = true;
    }

    /** Получает все интеракции в указанном временном периоде */
    getInteractionsBetween(start: DOMHighResTimeStamp, end: DOMHighResTimeStamp): Interaction[] {
        const result: Interaction[] = [];
        for (const interaction of this.buffer) {
            if (interaction.timeStamp < start) {
                continue;
            }

            if (interaction.timeStamp > end) {
                break;
            }

            result.push(interaction);
        }

        return result;
    }

    /** Получает ближайшую по времени интеракцию к событию с определенным типом */
    getClosestInteraction(timestamp: number, type?: string): Interaction | null {
        let minDelta = Infinity;
        let result = null;

        for (const event of this.buffer) {
            const currentDelta = timestamp - event.timeStamp;

            // Событие произошло после переданного времени
            if (currentDelta < 0) {
                break;
            }

            // Скипаем не интересующие нас типы интеракций
            if (type && event.eventType !== type) {
                continue;
            }

            if (minDelta > currentDelta) {
                result = event;
                minDelta = currentDelta;
            }
        }

        return result;
    }

    /** Получает все интеракции */
    getAllInteractions() {
        return [...this.buffer];
    }

    /** Освобождает задействованные ресурсы */
    dispose() {
        this.events.map(eventType => removeEventListener(eventType, this.handleEvent, { capture: true }));
    }
}
